/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import AiModelIcon from '@atlaskit/icon-lab/core/ai-model';
import AtomIcon from '@atlaskit/icon-lab/core/atom';
import RandomizeIcon from '@atlaskit/icon-lab/core/randomize';
import TelescopeIcon from '@atlaskit/icon-lab/core/telescope';
import EpicIcon from '@atlaskit/icon/core/epic';
import ScorecardIcon from '@atlaskit/icon/core/scorecard';
import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { ReasoningModeMenu as SharedReasoningModeMenu } from '@atlassian/rovo-platform-ui-components/reasoning-mode-menu';
import type { ReasoningModeMenuItem } from '@atlassian/rovo-platform-ui-components/reasoning-mode-menu/types';

import type { ChatMode, ReasoningMode } from '../../../controllers/chat-mode-store/types';

import { messages as customizationMenuMessages } from './messages';
import { reasoningModeMenuMessages } from './reasoningModeMenuMessages';
import { TaskModeMenuItemBackground } from './TaskModeMenuItemBackground';
import { TaskModeTriggerWrapper } from './TaskModeTriggerWrapper';

const styles = cssMap({
	menuWrapper: {
		marginRight: token('space.050'),
	},
});

type ReasoningModeMenuProps = {
	chatModeState: Pick<ChatMode, 'reasoningMode'> & Partial<ChatMode>;
	disabledTooltip?: string;
	onReasoningModeChange: (mode: ReasoningMode) => void;
	renderAutoApplyToggle?: () => React.ReactNode;
	isDisabled: boolean;
	enableMaxMode?: boolean;
	placement?: React.ComponentProps<typeof SharedReasoningModeMenu>['placement'];
	onMenuOpen?: () => void;
	onMenuOpenChange?: (isOpen: boolean) => void;
	shouldRenderInPortal?: boolean;
};

const lozengeChipStyles = cssMap({
	chip: {
		borderRadius: token('radius.small'),
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.discovery.bold'),
		display: 'inline-flex',
		alignItems: 'center',
	},
});

/**
 * Renders a styled lozenge chip matching the original ChatModeToggleMenuItem's
 * alphaChip style (purple background, white bold text).
 */
const LozengeChip = ({ label }: { label: string }) => (
	<span css={lozengeChipStyles.chip}>
		<Text size="small" weight="bold" color="color.text.inverse">
			{label}
		</Text>
	</span>
);

// Icon components keyed by reasoning mode — used for the trigger pill icon
const getReasoningModeIcon = (mode: ReasoningMode): React.ComponentType<{ label: string }> => {
	switch (mode) {
		case 'auto':
			return RandomizeIcon;
		case 'fast':
			return EpicIcon;
		case 'think-deeper':
		case 'think-deeper-marathon':
		case 'think-deeper-orchestrator':
			return AiModelIcon;
		case 'deep-research':
			return TelescopeIcon;
		case 'task':
			return AtomIcon;
	}
};

export const ReasoningModeMenu = ({
	chatModeState,
	onReasoningModeChange,
	renderAutoApplyToggle,
	isDisabled,
	enableMaxMode = true,
	disabledTooltip,
	placement,
	onMenuOpen,
	onMenuOpenChange,
	shouldRenderInPortal = false,
}: ReasoningModeMenuProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { reasoningMode } = chatModeState;
	const [isOpen, setIsOpen] = useState(false);

	// Avoid inheriting stale task mode state when Max mode is disabled.
	const allowedReasoningMode = reasoningMode === 'task' && !enableMaxMode ? 'auto' : reasoningMode;

	// When the tasks flag is on, DR is selected from the add menu and runs
	// silently — the pill should show "Auto" rather than "Researching".
	const isDeepResearchAsAddMenuMode =
		allowedReasoningMode === 'deep-research' && fg('rovo_chat_enable_tasks');
	const pillReasoningMode = isDeepResearchAsAddMenuMode ? 'auto' : allowedReasoningMode;

	const triggerLabel = formatMessage(
		pillReasoningMode === 'fast'
			? reasoningModeMenuMessages.fastLabel
			: pillReasoningMode === 'think-deeper' ||
				  pillReasoningMode === 'think-deeper-marathon' ||
				  pillReasoningMode === 'think-deeper-orchestrator'
				? reasoningModeMenuMessages.thinkingLabel
				: pillReasoningMode === 'deep-research'
					? reasoningModeMenuMessages.researchingLabel
					: pillReasoningMode === 'task'
						? reasoningModeMenuMessages.taskLabel
						: reasoningModeMenuMessages.autoLabel,
	);

	const TriggerIcon = getReasoningModeIcon(pillReasoningMode);

	// Feature gate checks — these are AI Mate–specific and stay here
	const isTDSupportedWithTenant = fg('rovo_chat_exclude_atlassian_hosted_models_orgs');
	const isExtendedThinkingVariantsEnabled = fg('rovo_chat_marathon_orchestrator_reasoning_modes');
	const isTaskModeEnabled = fg('rovo_chat_enable_tasks') && enableMaxMode;
	const isTaskModeDesignRefresh = fg('rovo_chat_task_mode_design_refresh');
	const isTaskTrigger = isTaskModeDesignRefresh && allowedReasoningMode === 'task';

	// Build the items array — feature gates + analytics + i18n all owned here
	const items: ReasoningModeMenuItem[] = useMemo(() => {
		const makeOnSelect = (mode: ReasoningMode, analyticsSubjectId: string) => () => {
			onReasoningModeChange(mode);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: analyticsSubjectId,
			});
		};

		const result: ReasoningModeMenuItem[] = [
			{
				id: 'auto',
				title: formatMessage(reasoningModeMenuMessages.autoTitle),
				subtitle: formatMessage(reasoningModeMenuMessages.autoDescription),
				icon: <RandomizeIcon spacing="spacious" label="" />,
				isSelected: allowedReasoningMode === 'auto',
				isDisabled: false,
				onSelect: makeOnSelect('auto', 'rovoChatAutoReasoningButton'),
			},
			{
				id: 'fast',
				title: formatMessage(reasoningModeMenuMessages.fastTitle),
				subtitle: formatMessage(reasoningModeMenuMessages.fastDescription),
				icon: <EpicIcon spacing="spacious" label="" />,
				isSelected: allowedReasoningMode === 'fast',
				isDisabled: false,
				onSelect: makeOnSelect('fast', 'rovoChatFastModeButton'),
			},
			{
				id: 'think-deeper',
				title: formatMessage(reasoningModeMenuMessages.thinkDeeperTitle),
				subtitle: formatMessage(reasoningModeMenuMessages.thinkDeeperDescription),
				icon: <AiModelIcon spacing="spacious" label="" />,
				isSelected: allowedReasoningMode === 'think-deeper',
				isDisabled: !isTDSupportedWithTenant,
				onSelect: makeOnSelect('think-deeper', 'rovoChatThinkDeeperButton'),
			},
		];

		if (isExtendedThinkingVariantsEnabled) {
			result.push(
				{
					id: 'think-deeper-marathon',
					title: formatMessage(reasoningModeMenuMessages.thinkDeeperMarathonTitle),
					subtitle: formatMessage(reasoningModeMenuMessages.thinkDeeperMarathonDescription),
					icon: <AiModelIcon spacing="spacious" label="" />,
					isSelected: allowedReasoningMode === 'think-deeper-marathon',
					isDisabled: false,
					lozengeLabel: (
						<LozengeChip label={formatMessage(reasoningModeMenuMessages.lozengeAlpha)} />
					),
					onSelect: makeOnSelect('think-deeper-marathon', 'rovoChatThinkDeeperMarathonButton'),
				},
				{
					id: 'think-deeper-orchestrator',
					title: formatMessage(reasoningModeMenuMessages.thinkDeeperOrchestratorTitle),
					subtitle: formatMessage(reasoningModeMenuMessages.thinkDeeperOrchestratorDescription),
					icon: <AiModelIcon spacing="spacious" label="" />,
					isSelected: allowedReasoningMode === 'think-deeper-orchestrator',
					isDisabled: false,
					lozengeLabel: (
						<LozengeChip label={formatMessage(reasoningModeMenuMessages.lozengeAlpha)} />
					),
					onSelect: makeOnSelect(
						'think-deeper-orchestrator',
						'rovoChatThinkDeeperOrchestratorButton',
					),
				},
			);
		}

		if (!isTaskModeEnabled) {
			result.push({
				id: 'deep-research',
				title: formatMessage(reasoningModeMenuMessages.deepResearchTitle),
				subtitle: formatMessage(reasoningModeMenuMessages.deepResearchDescription),
				icon: <TelescopeIcon spacing="spacious" label="" />,
				isSelected: allowedReasoningMode === 'deep-research',
				isDisabled: false,
				onSelect: makeOnSelect('deep-research', 'rovoChatDeepResearchButton'),
			});
		}

		if (isTaskModeEnabled) {
			if (isTaskModeDesignRefresh) {
				result.push({
					id: 'task',
					title: formatMessage(reasoningModeMenuMessages.taskTitle),
					subtitle: formatMessage(reasoningModeMenuMessages.taskModeDescriptionRefresh),
					icon: <AtomIcon spacing="spacious" label="" />,
					isSelected: allowedReasoningMode === 'task',
					isDisabled: false,
					lozengeLabel: <LozengeChip label={formatMessage(reasoningModeMenuMessages.lozengeNew)} />,
					onSelect: makeOnSelect('task', 'rovoChatTaskModeButton'),
					wrapper: TaskModeMenuItemBackground,
				});
			} else {
				result.push({
					id: 'task',
					title: formatMessage(reasoningModeMenuMessages.taskTitle),
					subtitle: formatMessage(reasoningModeMenuMessages.taskModeDescription),
					icon: <ScorecardIcon spacing="spacious" label="" />,
					isSelected: allowedReasoningMode === 'task',
					isDisabled: false,
					onSelect: makeOnSelect('task', 'rovoChatTaskModeButton'),
				});
			}
		}

		return result;
	}, [
		allowedReasoningMode,
		formatMessage,
		isExtendedThinkingVariantsEnabled,
		isTDSupportedWithTenant,
		isTaskModeDesignRefresh,
		isTaskModeEnabled,
		onReasoningModeChange,
		sendAnalyticsEvent,
	]);

	const onOpenChange = useCallback(
		({ isOpen: open }: { isOpen: boolean }) => {
			setIsOpen(open);
			onMenuOpenChange?.(open);
			if (open) {
				onMenuOpen?.();
				sendAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'rovoChatReasoningModeMenuTrigger',
				});
				sendAnalyticsEvent({
					action: 'viewed',
					actionSubject: 'menu',
					actionSubjectId: 'rovoChatReasoningModeMenu',
				});
			}
		},
		[onMenuOpen, onMenuOpenChange, sendAnalyticsEvent],
	);

	// Keep a ref so TooltipTriggerWrapper can always read the latest value without being recreated.
	const disabledTooltipRef = useRef(disabledTooltip);
	disabledTooltipRef.current = disabledTooltip;

	// Stable component type (empty deps) — reads from ref to avoid recreating on each change.
	const TooltipTriggerWrapper = useMemo(
		() =>
			({ children }: { children: React.ReactNode }) => (
				<Tooltip content={disabledTooltipRef.current}>{children}</Tooltip>
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	// Priority: disabled tooltip > task mode > none.
	const TriggerWrapper = useMemo(() => {
		if (isDisabled && disabledTooltip) {
			return TooltipTriggerWrapper;
		}
		if (isTaskTrigger) {
			return TaskModeTriggerWrapper;
		}
		return undefined;
	}, [isDisabled, disabledTooltip, isTaskTrigger, TooltipTriggerWrapper]);

	return (
		<div css={styles.menuWrapper}>
			<SharedReasoningModeMenu
				triggerLabel={triggerLabel}
				triggerIcon={TriggerIcon}
				triggerTestId="reasoning-mode-menu-trigger"
				items={items}
				isDisabled={isDisabled}
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				placement={placement || 'top-end'}
				shouldRenderToParent={!shouldRenderInPortal}
				renderFooter={renderAutoApplyToggle}
				headingLabel={formatMessage(customizationMenuMessages.reasoningSectionTitle)}
				triggerWrapper={TriggerWrapper}
			/>
		</div>
	);
};
